import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  SUPPORTED_LANGUAGES,
  changeLanguage,
  getCurrentLanguage
} from '../../locate/transifexService';
import './LanguageSwitcher.css';

/**
 * Expandable Globe Language Switcher Component
 * Placed in the top navigation bar next to the site brand icon.
 * Supports primary target languages: lat, eng, rus, esp, india (+ fr).
 */
const LanguageSwitcher = () => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState(() => getCurrentLanguage());
  const dropdownRef = useRef(null);

  // Sync internal state when i18n language changes
  useEffect(() => {
    if (i18n && i18n.language) {
      setCurrentLang(getCurrentLanguage());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  // Handle outside click to close dropdown
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleSelectLanguage = async (code) => {
    await changeLanguage(code);
    setCurrentLang(code);
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    setIsOpen(prev => !prev);
  };

  const currentMeta = SUPPORTED_LANGUAGES.find(l => l.code === currentLang) || SUPPORTED_LANGUAGES[0];

  return (
    <div className="language-switcher ms-2" ref={dropdownRef}>
      <button
        type="button"
        className="language-switcher-btn"
        onClick={toggleDropdown}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={t('nav.selectLanguage', 'Select Language')}
        title={t('nav.selectLanguage', 'Select Language')}
        data-testid="language-switcher-btn"
      >
        <FontAwesomeIcon icon="globe" className="globe-icon" />
        <span className="lang-code">{currentMeta.code.toUpperCase()}</span>
      </button>

      {isOpen && (
        <div
          className="language-switcher-menu"
          role="menu"
          aria-orientation="vertical"
          data-testid="language-switcher-menu"
        >
          <div className="language-menu-header">
            {t('nav.selectLanguage', 'Select Language')}
          </div>
          {SUPPORTED_LANGUAGES.map((lang) => {
            const isActive = lang.code === currentLang;
            return (
              <button
                key={lang.code}
                type="button"
                role="menuitem"
                className={`language-menu-item ${isActive ? 'active' : ''}`}
                onClick={() => handleSelectLanguage(lang.code)}
                data-testid={`lang-option-${lang.code}`}
                aria-current={isActive ? 'true' : 'false'}
              >
                <div className="language-item-left">
                  <img
                    src={lang.flag}
                    alt={`${lang.name} flag`}
                    className="language-flag-img"
                  />
                  <div>
                    <span className="language-name-native">{lang.nativeName}</span>
                    <span className="language-name-en">({lang.name})</span>
                  </div>
                </div>
                {isActive && <span className="language-check" aria-hidden="true">✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
